import { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
    Badge,
    Button,
    Input,
    MessageBarActions,
    MessageBarBody,
    MessageBarTitle,
    SearchBox,
    Spinner,
    Textarea
} from '@fluentui/react-components';
import { Add16Regular, Delete16Regular, Edit16Regular, Tag20Regular } from '@fluentui/react-icons';

import * as ComponentFunctions from './ContractConceptCategories.Functions';
import { CategoryDraft, DraftErrors, draftOf, filterCategories, hasErrors, isInUseError, toPayload, validateDraft } from './ContractConceptCategories.Logic';
import { ContractConceptCategory } from '../../../../models/Entities/Contracts/ContractConceptCategory';
import { GuideDrawer, GuideDrawerSection } from '../../../../theme/components/GuideDrawer';
import { GuideField } from '../../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';

import './ContractConceptCategories.Styles.css';

type Mode = { kind: 'idle' } | { kind: 'new' } | { kind: 'edit'; category: ContractConceptCategory };

/**
 * Alta, edición y baja de las categorías de concepto de contrato del tenant.
 * El formulario va dentro del mismo drawer, arriba de la lista: no hay un
 * segundo panel encima.
 */
export const ContractConceptCategoriesDrawerComponent = (props: ComponentFunctions.Props) => {
    const [mode, setMode] = useState<Mode>({ kind: 'idle' });
    const [draft, setDraft] = useState<CategoryDraft>(draftOf());
    const [touched, setTouched] = useState(false);
    const [search, setSearch] = useState('');
    const [confirmDeleteId, setConfirmDeleteId] = useState<number | undefined>(undefined);
    const nameRef = useRef<HTMLInputElement | null>(null);

    // Al abrir: lista fresca y sin restos de la vez anterior.
    useEffect(() => {
        if (!props.isOpen) return;
        props.ResetContractConceptCategoryFlags();
        props.GetAllContractConceptCategories(true);
        setMode({ kind: 'idle' });
        setSearch('');
        setConfirmDeleteId(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen]);

    // Guardado: el formulario se cierra solo.
    useEffect(() => {
        if (props.savedOk && mode.kind !== 'idle') {
            setMode({ kind: 'idle' });
            setDraft(draftOf());
            setTouched(false);
            props.ResetContractConceptCategoryFlags();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.savedOk]);

    useEffect(() => {
        if (props.deletedOk) setConfirmDeleteId(undefined);
    }, [props.deletedOk]);

    useEffect(() => {
        if (mode.kind !== 'idle') nameRef.current?.focus();
    }, [mode]);

    const editingId = mode.kind === 'edit' ? mode.category.id : undefined;
    const errors: DraftErrors = useMemo(() => validateDraft(draft, props.Categories, editingId), [draft, props.Categories, editingId]);
    const visible = useMemo(() => filterCategories(props.Categories, search), [props.Categories, search]);

    const startNew = () => { props.ResetContractConceptCategoryFlags(); setDraft(draftOf()); setTouched(false); setMode({ kind: 'new' }); };
    const startEdit = (category: ContractConceptCategory) => { props.ResetContractConceptCategoryFlags(); setDraft(draftOf(category)); setTouched(false); setMode({ kind: 'edit', category }); };
    const cancelForm = () => { setMode({ kind: 'idle' }); setDraft(draftOf()); setTouched(false); props.ResetContractConceptCategoryFlags(); };

    const save = () => {
        setTouched(true);
        if (hasErrors(errors) || props.isSaving) return;
        const payload = toPayload(draft);
        if (mode.kind === 'edit') props.UpdateContractConceptCategory({ ...mode.category, ...payload });
        else props.AddContractConceptCategory(payload);
    };

    const deleteError = props.failOnDeleting && props.deletingId !== undefined
        ? (isInUseError(props.error) ? 'No se puede eliminar: hay conceptos de contrato que la usan.' : (props.error?.ErrorMessage || 'No se pudo eliminar la categoría.'))
        : undefined;

    const formTitle = mode.kind === 'edit' ? 'Editar categoría' : 'Nueva categoría';
    const nameError = touched && errors.name ? errors.name : undefined;

    return (
        <GuideDrawer
            open={props.isOpen}
            size="large"
            title="Categorías de conceptos"
            subtitle="Agrupan los conceptos del contrato para filtrarlos y resumirlos. Una categoría en uso no se puede eliminar."
            onDismiss={props.onClose}
            closeLabel="Cerrar"
            secondaryAction={{ label: 'Cerrar' }}
            className="ccc-drawer"
            testId="contract-concept-categories-drawer"
        >
            <div className="ccc-toolbar">
                <SearchBox
                    className="ccc-search"
                    value={search}
                    onChange={(_, data) => setSearch(data.value)}
                    placeholder="Buscar..."
                    aria-label="Buscar categoría"
                />
                <Badge appearance="filled" color="informative" shape="circular" size="large">
                    {props.Categories.length} {props.Categories.length === 1 ? 'categoría' : 'categorías'}
                </Badge>
                <Button appearance="primary" icon={<Add16Regular />} onClick={startNew} disabled={mode.kind === 'new'}>
                    Nueva categoría
                </Button>
            </div>

            {mode.kind !== 'idle' && (
                <GuideDrawerSection title={formTitle} className="ccc-form" testId="ccc-form">
                    <GuideField
                        label="Nombre"
                        required
                        error={nameError}
                        hint={nameError ? undefined : 'Como se va a ver en los conceptos y en los filtros.'}
                    >
                        <Input
                            ref={nameRef}
                            value={draft.name}
                            maxLength={200}
                            onChange={(_, data) => setDraft((current) => ({ ...current, name: data.value }))}
                            onKeyDown={(event) => { if (event.key === 'Enter') save(); if (event.key === 'Escape') cancelForm(); }}
                            placeholder="Ej. Visitas, Procedimientos, Insumos"
                        />
                    </GuideField>
                    <GuideField label="Descripción" error={touched && errors.description ? errors.description : undefined}>
                        <Textarea
                            value={draft.description}
                            maxLength={500}
                            rows={2}
                            resize="vertical"
                            onChange={(_, data) => setDraft((current) => ({ ...current, description: data.value }))}
                            placeholder="Opcional"
                        />
                    </GuideField>
                    {props.failOnSaving && (
                        <GuideMessageBar intent="error">
                            <MessageBarBody>
                                <MessageBarTitle>No se pudo guardar.</MessageBarTitle> {props.error?.ErrorMessage || 'Probá de nuevo.'}
                                {props.error?.Errors && props.error.Errors.length > 0 && <ul>{props.error.Errors.map((item) => <li key={item}>{item}</li>)}</ul>}
                            </MessageBarBody>
                        </GuideMessageBar>
                    )}
                    <div className="ccc-form__actions">
                        <Button appearance="secondary" onClick={cancelForm} disabled={props.isSaving}>Cancelar</Button>
                        <Button
                            appearance="primary"
                            onClick={save}
                            disabled={props.isSaving || (touched && hasErrors(errors))}
                            icon={props.isSaving ? <Spinner size="extra-tiny" /> : undefined}
                        >
                            {mode.kind === 'edit' ? 'Guardar cambios' : 'Crear categoría'}
                        </Button>
                    </div>
                </GuideDrawerSection>
            )}

            {deleteError && (
                <GuideMessageBar intent="error">
                    <MessageBarBody>{deleteError}</MessageBarBody>
                </GuideMessageBar>
            )}

            {props.isLoading && props.Categories.length === 0 && (
                <Spinner size="extra-tiny" labelPosition="after" label="Cargando categorías…" />
            )}

            {/* Un GET fallido no es "no hay categorías": se dice y se puede reintentar */}
            {!props.isLoading && props.failOnLoading && (
                <GuideMessageBar intent="error">
                    <MessageBarBody>
                        <MessageBarTitle>No se pudieron cargar las categorías.</MessageBarTitle> {props.error?.ErrorMessage || ''}
                    </MessageBarBody>
                    <MessageBarActions>
                        <Button onClick={() => props.GetAllContractConceptCategories(true)}>Reintentar</Button>
                    </MessageBarActions>
                </GuideMessageBar>
            )}

            {!props.isLoading && props.hasLoaded && !props.failOnLoading && props.Categories.length === 0 && mode.kind === 'idle' && (
                <div className="ccc-empty">
                    <Tag20Regular />
                    <span className="ccc-empty__title">Todavía no hay categorías</span>
                    <span className="ccc-muted">Creá la primera para empezar a agrupar los conceptos del contrato.</span>
                    <Button appearance="primary" icon={<Add16Regular />} onClick={startNew}>Nueva categoría</Button>
                </div>
            )}

            {props.Categories.length > 0 && visible.length === 0 && (
                <div className="ccc-muted">Ninguna categoría coincide con “{search}”.</div>
            )}

            {visible.length > 0 && (
                <ul className="ccc-list">
                    {visible.map((category) => {
                        const isEditing = editingId === category.id;
                        const isDeletingThis = props.isDeleting && props.deletingId === category.id;
                        const askingDelete = confirmDeleteId === category.id;
                        return (
                            <li key={category.id} className={`ccc-row${isEditing ? ' ccc-row--editing' : ''}`}>
                                <span className="ccc-row__icon" aria-hidden><Tag20Regular /></span>
                                <div className="ccc-row__body">
                                    <span className="ccc-row__name" title={category.name}>{category.name}</span>
                                    {category.description && <span className="ccc-row__desc" title={category.description}>{category.description}</span>}
                                    {askingDelete && (
                                        <div className="ccc-confirm">
                                            <span>¿Eliminar “{category.name}”?</span>
                                            <Button
                                                size="small"
                                                appearance="primary"
                                                className="ccc-btn--danger"
                                                disabled={isDeletingThis}
                                                icon={isDeletingThis ? <Spinner size="extra-tiny" /> : undefined}
                                                onClick={() => props.DeleteContractConceptCategory(category.id)}
                                            >
                                                Eliminar
                                            </Button>
                                            <Button size="small" disabled={isDeletingThis} onClick={() => setConfirmDeleteId(undefined)}>Cancelar</Button>
                                        </div>
                                    )}
                                </div>
                                {!askingDelete && (
                                    <span className="ccc-row__actions">
                                        <Button
                                            appearance="subtle"
                                            size="small"
                                            title="Editar"
                                            aria-label={`Editar ${category.name}`}
                                            icon={<Edit16Regular />}
                                            onClick={() => startEdit(category)}
                                            disabled={props.isDeleting}
                                        />
                                        <Button
                                            appearance="subtle"
                                            size="small"
                                            className="ccc-icon-btn--danger"
                                            title="Eliminar"
                                            aria-label={`Eliminar ${category.name}`}
                                            icon={<Delete16Regular />}
                                            onClick={() => { props.ResetContractConceptCategoryFlags(); setConfirmDeleteId(category.id); }}
                                            disabled={props.isDeleting}
                                        />
                                    </span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </GuideDrawer>
    );
};

export default connect(ComponentFunctions.mapStateToProps, ComponentFunctions.mapDispatchToProps)(ContractConceptCategoriesDrawerComponent as any) as React.ComponentType<ComponentFunctions.IProps>;
